/**
 * AppStats Logo Component
 * Professional logo with multiple variants
 */

import { ComponentProps } from 'react'

interface LogoProps extends Omit<ComponentProps<'div'>, 'children'> {
  variant?: 'full' | 'icon' | 'wordmark'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  gradient?: boolean
  animated?: boolean
}

const sizeMap = {
  sm: { icon: 32, text: 'text-lg' },
  md: { icon: 40, text: 'text-xl' },
  lg: { icon: 56, text: 'text-3xl' },
  xl: { icon: 80, text: 'text-5xl' },
}

export default function Logo({ 
  variant = 'full', 
  size = 'md',
  gradient = false,
  animated = false,
  className = '',
  ...props 
}: LogoProps) {
  const { icon: iconSize, text: textSize } = sizeMap[size]
  
  const LogoIcon = () => (
    <img 
      src="/logo.png" 
      alt="AppStats" 
      width={iconSize} 
      height={iconSize}
      className={`rounded-2xl ${animated ? 'animate-pulse-soft' : ''}`}
      style={{ objectFit: 'contain' }}
    />
  )
  
  const Wordmark = () => (
    <div className={`${textSize} font-bold tracking-tight ${gradient ? 'gradient-text' : ''}`}>
      AppStats
    </div>
  )
  
  if (variant === 'icon') {
    return (
      <div className={`inline-flex ${className}`} {...props}>
        <LogoIcon />
      </div>
    )
  }
  
  if (variant === 'wordmark') {
    return (
      <div className={`inline-flex ${className}`} {...props}>
        <Wordmark />
      </div>
    )
  }
  
  // Full logo (icon + wordmark)
  return (
    <div className={`inline-flex items-center gap-3 ${className}`} {...props}>
      <LogoIcon />
      <div>
        <Wordmark />
        <p className="text-xs text-gray-400 mt-0.5" style={{ fontSize: size === 'sm' ? '10px' : size === 'md' ? '11px' : '13px' }}>
          Analytics
        </p>
      </div>
    </div>
  )
}
